// Main-thread bridge to the upload Web Worker.
//
// It mimics the slice of the resumablejs `Resumable` API that
// components/Upload/index.tsx actually uses — `.on(event, cb)`, `.files`,
// `.opts.query`, `.opts.headers`, `.upload()`, `.assignDrop()`,
// `.assignBrowse()` — so index.tsx can switch to it behind a flag with minimal
// change and every downstream consumer/guard keeps working unchanged.
//
// The hard requirement (verified in Create/Video.tsx, UploadProgress.tsx): the
// object handed to consumers must look like a resumablejs ResumableFile —
// `.file` (raw File), `.fileName`, `.uniqueIdentifier`, `.relativePath`, a
// `.progress(relative)` METHOD, `.cancel()`, `.isComplete()` — and be freely
// mutable (consumers set `.isShorts`, `.type`, `.aspect_ratio`, …). WorkerFile
// below reproduces that shape.

type EventName =
  | "fileAdded"
  | "fileProgress"
  | "fileSuccess"
  | "fileError"
  | "fileRetry";

export interface WorkerEngineConfig {
  target: string;
  query?: Record<string, any>;
  headers?: Record<string, string>;
  chunkSize?: number;
  simultaneousUploads?: number;
  maxChunkRetries?: number;
  chunkRetryInterval?: number;
  permanentErrors?: number[];
  generateUniqueIdentifier?: (file: File) => string;
}

export interface ChunkInfoItem {
  number: number; // 1-based, matches resumableChunkNumber
  size: number;
}

// The consumer-facing file object. Mirrors resumablejs ResumableFile closely
// enough that Create/Video.tsx, CreateOptions.tsx, Audio.tsx and
// UploadProgress.tsx treat it identically to a real one.
export class WorkerFile {
  file: File;
  fileName: string;
  relativePath: string;
  uniqueIdentifier: string;
  // Mirrors resumablejs' ResumableFile.size (resumable.js: `$.size = file.size`).
  // Callers (e.g. CreateOptions.tsx's onFileEvent) read size straight off the
  // fileAdded object rather than object.file.size, matching that convention.
  size: number;
  // Reupload only: chunks the server already has for this upload_id (from
  // asset.chunkinfo). Set by the caller after addFileWithIdentifier() returns,
  // before upload() is called. Undefined for every other call site.
  uploadedChunks?: ChunkInfoItem[];

  // Progress state, updated from worker "progress" messages.
  private _loaded = 0;
  private _total = 0;
  private _complete = false;
  private _errored = false;

  // Set by the engine so cancel()/upload-start can reach the worker.
  _engine: WorkerUploadEngine | null = null;

  // Index signature so consumers can freely attach fields (isShorts, type, …).
  [key: string]: any;

  constructor(file: File, uniqueIdentifier: string) {
    this.file = file;
    // resumablejs precedence for the display/relative names.
    this.fileName =
      (file as any).webkitRelativePath || (file as any).fileName || file.name || "";
    this.relativePath = (file as any).webkitRelativePath || this.fileName;
    this.uniqueIdentifier = uniqueIdentifier;
    this.size = file.size;
    this._total = file.size;
  }

  _setProgress(loaded: number, total: number) {
    this._loaded = loaded;
    this._total = total || this._total;
  }
  _setComplete() {
    this._complete = true;
    this._loaded = this._total;
  }
  _setErrored() {
    this._errored = true;
  }

  // Same semantics as resumablejs ResumableFile.progress(relative):
  // returns 0..1. Consumers call file.progress(false).
  progress(_relative?: boolean): number {
    if (this._errored) return 1;
    if (this._complete) return 1;
    if (!this._total) return 0;
    return Math.min(1, this._loaded / this._total);
  }

  isComplete(): boolean {
    return this._complete;
  }

  cancel(): void {
    this._engine?._cancel(this);
  }
}

export class WorkerUploadEngine {
  private worker: Worker;
  private cfg: WorkerEngineConfig;
  private handlers: Record<EventName, Array<(...args: any[]) => void>> = {
    fileAdded: [],
    fileProgress: [],
    fileSuccess: [],
    fileError: [],
    fileRetry: [],
  };

  // Active files keyed by uniqueIdentifier — the same key space as the
  // resumableFiles registry in index.tsx.
  private byId = new Map<string, WorkerFile>();

  // Files queued but not yet started, so index.tsx can inspect/replace them
  // before calling upload() (mirrors resumable.files + resumable.upload()).
  files: WorkerFile[] = [];

  // Mirror of resumable.opts.{query,headers}; index.tsx mutates these before
  // upload(), exactly as it does with the resumablejs instance.
  opts: { query: Record<string, any>; headers: Record<string, string> };

  private input: HTMLInputElement | null = null;

  constructor(cfg: WorkerEngineConfig) {
    this.cfg = cfg;
    this.opts = {
      query: { ...(cfg.query || {}) },
      headers: { ...(cfg.headers || {}) },
    };

    this.worker = new Worker(new URL("./upload.worker.ts", import.meta.url), {
      type: "module",
    });
    console.log(
      "%c[upload] Web Worker engine ACTIVE",
      "color:#0d9488;font-weight:bold",
      "— chunk uploads run off the main thread"
    );
    this.worker.onmessage = this.onWorkerMessage;

    this.worker.postMessage({
      type: "init",
      config: {
        target: cfg.target,
        chunkSize: cfg.chunkSize,
        simultaneousUploads: cfg.simultaneousUploads,
        maxChunkRetries: cfg.maxChunkRetries,
        chunkRetryInterval: cfg.chunkRetryInterval,
        permanentErrors: cfg.permanentErrors,
      },
    });
  }

  on(event: EventName, cb: (...args: any[]) => void): void {
    this.handlers[event]?.push(cb);
  }

  private fire(event: EventName, ...args: any[]) {
    this.handlers[event]?.forEach((cb) => cb(...args));
  }

  private genId(file: File): string {
    if (this.cfg.generateUniqueIdentifier) return this.cfg.generateUniqueIdentifier(file);
    const name = (file as any).webkitRelativePath || (file as any).fileName || file.name || "";
    const sanitized = name.replace(/[^0-9a-zA-Z_-]/gim, "");
    return `${file.size}-${sanitized}`;
  }

  // Called when a file is picked/dropped. Creates the WorkerFile, queues it,
  // and fires "fileAdded" — same lifecycle point as resumablejs.
  private addFile(raw: File) {
    const id = this.genId(raw);
    const wf = new WorkerFile(raw, id);
    wf._engine = this;
    this.files.push(wf);
    this.fire("fileAdded", wf);
  }

  // Programmatic equivalent of resumablejs' `resumable.addFile(file)`, used by
  // call sites that pick a File outside the drop-zone/browse DOM flow (e.g. a
  // hidden <input type="file"> onChange) and want a specific identifier so it
  // lines up with a Redux key computed before upload() is called.
  //
  // uploadedChunks must be set BEFORE fire("fileAdded", wf): callers' fileAdded
  // handlers commonly call upload() synchronously (see RenderAction's reupload
  // flow), which reads wf.uploadedChunks at postMessage time — setting it after
  // this call returns would be too late.
  addFileWithIdentifier(
    raw: File,
    uniqueIdentifier: string,
    uploadedChunks?: ChunkInfoItem[]
  ): WorkerFile {
    const wf = new WorkerFile(raw, uniqueIdentifier);
    wf._engine = this;
    wf.uploadedChunks = uploadedChunks;
    this.files.push(wf);
    this.fire("fileAdded", wf);
    return wf;
  }

  // Starts uploading every queued file that isn't already active. index.tsx
  // sets opts.query / opts.headers first, exactly like the resumablejs path.
  upload(): void {
    for (const wf of this.files) {
      if (this.byId.has(wf.uniqueIdentifier)) continue;
      this.byId.set(wf.uniqueIdentifier, wf);
      this.worker.postMessage({
        type: "upload",
        id: wf.uniqueIdentifier,
        file: wf.file,
        fileName: wf.fileName,
        relativePath: wf.relativePath,
        uniqueIdentifier: wf.uniqueIdentifier,
        query: { ...this.opts.query },
        headers: { ...this.opts.headers },
        uploadedChunks: wf.uploadedChunks,
      });
    }
  }

  // Live token refresh across the worker boundary (used only if the app rotates
  // the token mid-upload; parity default is 401 -> permanent error -> fileError).
  updateToken(uniqueIdentifier: string, token: string): void {
    this.worker.postMessage({ type: "updateToken", id: uniqueIdentifier, token });
  }

  _cancel(wf: WorkerFile): void {
    this.worker.postMessage({ type: "cancel", id: wf.uniqueIdentifier });
    this.removeFromQueue(wf);
    this.byId.delete(wf.uniqueIdentifier);
  }

  private removeFromQueue(wf: WorkerFile) {
    const i = this.files.indexOf(wf);
    if (i >= 0) this.files.splice(i, 1);
  }

  private onWorkerMessage = (e: MessageEvent) => {
    const msg = e.data;
    const wf = this.byId.get(msg.id);
    if (!wf) return;

    switch (msg.type) {
      case "progress":
        wf._setProgress(msg.loaded, msg.total);
        this.fire("fileProgress", wf);
        break;
      case "success":
        wf._setComplete();
        this.removeFromQueue(wf);
        this.byId.delete(wf.uniqueIdentifier);
        // message is the raw server response body, matching resumablejs.
        this.fire("fileSuccess", wf, msg.message);
        break;
      case "error":
        wf._setErrored();
        this.removeFromQueue(wf);
        this.byId.delete(wf.uniqueIdentifier);
        this.fire("fileError", wf, msg.message);
        break;
      case "retry":
        this.fire("fileRetry", wf);
        break;
    }
  };

  // --- DOM helpers replacing resumablejs assignDrop / assignBrowse ---

  assignDrop(node: HTMLElement): void {
    node.addEventListener("dragover", this.onDragOver);
    node.addEventListener("drop", this.onDrop);
  }

  assignBrowse(node: HTMLElement): void {
    // Reuse a single hidden <input> per engine, like resumablejs does.
    if (!this.input) {
      const input = document.createElement("input");
      input.type = "file";
      input.style.display = "none";
      input.addEventListener("change", this.onInputChange);
      node.appendChild(input);
      this.input = input;
    }
    node.addEventListener("click", this.onBrowseClick);
  }

  private onBrowseClick = (e: Event) => {
    // Don't hijack clicks on real inputs/labels inside the dropzone.
    const target = e.target as HTMLElement;
    if (target && (target.tagName === "INPUT" || target.tagName === "LABEL")) return;
    this.input?.click();
  };

  private onInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    files.forEach((f) => this.addFile(f));
    input.value = ""; // allow re-picking the same file
  };

  private onDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  private onDrop = (e: DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    files.forEach((f) => this.addFile(f));
  };

  destroy(): void {
    this.worker.terminate();
  }
}
